import { useState, useCallback, useMemo } from 'react';
import { Column } from '@material-table/core';
import { PageSizeOptionEnum } from '../../enums';
import TablePreserverCommonInitialTableValues
  from '../tablePreservation/TablePreserverCommonInitialTableValues';

interface IColumnWithCustomSort<T extends object> extends Column<T> {
  customSort: () => number;
}

export interface IUseTableProps<RowData extends object> {
  initialActivePage?: IUseTableReturn<RowData>['activePage'];
  initialPerPage?: IUseTableReturn<RowData>['perPage'];
  initialColumnOrderBy?: IUseTableReturn<RowData>['columnOrderBy'];
  initialColumnOrderDirection?: IUseTableReturn<RowData>['columnOrderDirection'];
}

export type TTableColumnOrderDirection = 'asc' | 'desc' | '';

export interface IUseTableResetTableMethodsReturn {
  resetActivePage: () => void;
  resetPerPage: () => void;
  resetColumnOrderBy: () => void;
  resetColumnOrderDirection: () => void;
  resetSelectedRows: () => void;
  resetTable: () => void;
}

export interface IUseTableReturn<RowData extends object> {
  activePage: number;
  setActivePage: (value: number) => void;
  perPage: PageSizeOptionEnum;
  onPerPageChange: (newPerPage: PageSizeOptionEnum) => void;
  columnOrderBy: string | undefined;
  onColumnOrderByChange: (column: string | undefined) => void;
  columnOrderDirection: TTableColumnOrderDirection | undefined;
  onColumnOrderDirectionChange: (direction: TTableColumnOrderDirection) => void;
  selectedRows: Map<string, RowData>;
  onRowSelectionChange: (selection: Map<string, RowData>) => void | Promise<RowData>;
  createColumns: (columns: Column<RowData>[]) => IColumnWithCustomSort<RowData>[];
  resetTableMethods: IUseTableResetTableMethodsReturn;
}

const useTable = <RowData extends object>(
  props?: IUseTableProps<RowData>,
) : IUseTableReturn<RowData> => {
  const {
    initialActivePage = TablePreserverCommonInitialTableValues.activePage,
    initialPerPage = TablePreserverCommonInitialTableValues.perPage,
    initialColumnOrderBy = TablePreserverCommonInitialTableValues.columnOrderBy,
    initialColumnOrderDirection = TablePreserverCommonInitialTableValues.columnOrderDirection,
  } = props ?? {};

  const [activePage, setActivePage] = useState<
    IUseTableReturn<RowData>['activePage']
  >(initialActivePage);
  const [perPage, setPerPage] = useState<IUseTableReturn<RowData>['perPage']>(initialPerPage);
  const [columnOrderBy, setColumnOrderBy] = useState<IUseTableReturn<RowData>['columnOrderBy']>(
    () => (initialColumnOrderDirection
      ? initialColumnOrderBy
      : TablePreserverCommonInitialTableValues.columnOrderBy),
  );
  const [columnOrderDirection, setColumnOrderDirection] = useState<
    IUseTableReturn<RowData>['columnOrderDirection']
  >(
    () => (initialColumnOrderBy
      ? initialColumnOrderDirection
      : TablePreserverCommonInitialTableValues.columnOrderDirection),
  );
  const [selectedRows, setSelectedRows] = useState<
    IUseTableReturn<RowData>['selectedRows']
  >(new Map());

  const resetTableMethods = useMemo<IUseTableResetTableMethodsReturn>(() => {
    const resetActivePage = () => {
      setActivePage(TablePreserverCommonInitialTableValues.activePage);
    };
    const resetPerPage = () => {
      setPerPage(TablePreserverCommonInitialTableValues.perPage);
    };
    const resetColumnOrderBy = () => {
      setColumnOrderBy(TablePreserverCommonInitialTableValues.columnOrderBy);
    };
    const resetColumnOrderDirection = () => {
      setColumnOrderDirection(TablePreserverCommonInitialTableValues.columnOrderDirection);
    };
    const resetSelectedRows = () => {
      setSelectedRows(new Map());
    };
    const resetTable = () => {
      resetActivePage();
      resetPerPage();
      resetColumnOrderBy();
      resetColumnOrderDirection();
      resetSelectedRows();
    };
    return {
      resetActivePage,
      resetPerPage,
      resetColumnOrderBy,
      resetColumnOrderDirection,
      resetSelectedRows,
      resetTable,
    };
  }, []);

  const onPerPageChange = useCallback<IUseTableReturn<RowData>['onPerPageChange']>((newPerPage) => {
    setPerPage(newPerPage);
    resetTableMethods.resetActivePage();
  }, [resetTableMethods]);

  const onColumnOrderByChange = useCallback<IUseTableReturn<RowData>['onColumnOrderByChange']>(
    (column) => setColumnOrderBy(column),
    [],
  );
  const onColumnOrderDirectionChange = useCallback<
    IUseTableReturn<RowData>['onColumnOrderDirectionChange']
  >(
    (direction) => setColumnOrderDirection(direction),
    [],
  );
  const onRowSelectionChange = useCallback<IUseTableReturn<RowData>['onRowSelectionChange']>(
    (selection) => setSelectedRows(selection),
    [],
  );
  const createColumns = useCallback<IUseTableReturn<RowData>['createColumns']>(
    (columns) => columns.map((column) => {
      const processedColumn = {
        ...column,
        // todo change when bug is fixed https://github.com/material-table-core/core/issues/384
        customSort: () => 0,
      };
      if (processedColumn.field === columnOrderBy && !!columnOrderDirection) {
        processedColumn.defaultSort = columnOrderDirection;
      }
      return processedColumn;
    }),
    [columnOrderBy, columnOrderDirection],
  );

  return {
    activePage,
    setActivePage,
    perPage,
    onPerPageChange,
    columnOrderBy,
    onColumnOrderByChange,
    columnOrderDirection,
    onColumnOrderDirectionChange,
    selectedRows,
    onRowSelectionChange,
    createColumns,
    resetTableMethods,
  };
};

export default useTable;
